import React, { useState, useRef, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import MiniPingChart from "./MiniPingChart";

interface FloatMiniPingChartProps {
  uuid: string;
  trigger: React.ReactNode;
  chartWidth?: string | number;
  chartHeight?: string | number;
  hours?: number;
  onHoursChange?: (hours: 1 | 24) => void;
}

const MiniPingChartFloat: React.FC<FloatMiniPingChartProps> = ({
  uuid,
  trigger,
  chartWidth = 600,
  chartHeight = 300,
  hours = 1,
  onHoursChange,
}) => {
  const [open, setOpen] = useState(false);
  const [selectedHours, setSelectedHours] = useState<1 | 24>(hours === 1 ? 1 : 24);
  const hoverTimeoutRef = useRef<number | null>(null);
  const [t] = useTranslation();

  useEffect(() => {
    setSelectedHours(hours === 1 ? 1 : 24);
  }, [hours]);

  const handleMouseEnter = useCallback(() => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    hoverTimeoutRef.current = window.setTimeout(() => {
      setOpen(true);
    }, 3000);
  }, []);

  const handleMouseLeave = useCallback(() => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    hoverTimeoutRef.current = window.setTimeout(() => {
      setOpen(false);
    }, 200);
  }, []);

  const handleClick = useCallback(() => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    setOpen((prev) => !prev);
  }, []);

  const responsiveWidth = typeof chartWidth === 'number' ? `${chartWidth}px` : chartWidth;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <span
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onClick={handleClick}
          style={{ cursor: "pointer" }}
          className="flex items-center justify-center"
        >
          {trigger}
        </span>
      </PopoverTrigger>
      <PopoverContent
        sideOffset={5}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className="z-50 mx-auto w-auto max-w-[calc(100vw-1rem)] overflow-y-auto overscroll-contain rounded-lg border bg-card p-2 shadow-lg touch-pan-y"
        style={{
          width: responsiveWidth,
          maxHeight: "min(var(--radix-popover-content-available-height, calc(100dvh - 1rem)), calc(100dvh - 1rem))",
        }}
        align="center"
      >
        <div className="mb-2 flex items-center justify-between gap-2 px-1">
          <span className="text-xs text-muted-foreground">
            {t("nodeCard.ping", { defaultValue: "Latency" })}
          </span>
          <div className="flex items-center gap-1 rounded-md bg-muted/60 p-0.5">
            {([1, 24] as const).map((range) => (
              <Button
                key={range}
                type="button"
                size="sm"
                variant={selectedHours === range ? "default" : "ghost"}
                className={`h-7 min-w-12 px-2 text-xs transition-all ${
                  selectedHours === range
                    ? "bg-foreground text-background shadow-sm ring-1 ring-foreground/20 hover:bg-foreground/90 hover:text-background"
                    : "text-muted-foreground hover:text-foreground"
                }`}
                aria-pressed={selectedHours === range}
                onClick={() => {
                  setSelectedHours(range);
                  onHoursChange?.(range);
                }}
              >
                {range}h
              </Button>
            ))}
          </div>
        </div>
        <MiniPingChart hours={selectedHours} uuid={uuid} width="100%" height={chartHeight} />
      </PopoverContent>
    </Popover>
  );
};

export default MiniPingChartFloat;
